import { figure } from "@cliqsuite/ui";
import { useMemo } from "react";
import { useAdminData } from "../state/AdminDataContext";
import { mmk } from "../lib/format";
import { PageHeaderActions } from "../components/PageHeaderActions";
import { downloadCsv } from "../lib/csv";

const esc = (v: string) => v.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch]!);

/** One invoice as a standalone HTML file: opens in any browser and prints to PDF from there. */
function downloadInvoice(r: { invoice: string; business: string; email: string; plan: string; period: string; method: string; txid: string; paidOn: string; amount: number }) {
  const line = (k: string, v: string) => `<tr><th>${k}</th><td>${esc(v || "—")}</td></tr>`;
  const html = `<!doctype html><meta charset="utf-8"><title>Invoice ${esc(r.invoice)}</title>
<style>body{font:15px/1.5 system-ui,sans-serif;color:#20242E;max-width:560px;margin:48px auto;padding:0 20px}h1{font-size:28px;margin:0}table{width:100%;border-collapse:collapse;margin-top:24px}th,td{text-align:left;padding:10px 0;border-bottom:1px solid #E9EAEE}th{color:#5F6470;font-weight:600;width:40%}.total td,.total th{font-size:18px;font-weight:800;color:#20242E;border:none}</style>
<p style="font-weight:800;color:#DC2D49">CliqSuite</p><h1>Invoice ${esc(r.invoice)}</h1><p>Paid ${esc(r.paidOn)}</p>
<table>${line("Billed to", r.business)}${line("Billing email", r.email)}${line("Plan", r.plan)}${line("Period", r.period)}${line("Paid with", r.method)}${line("Transaction ID", r.txid)}<tr class="total"><th>Total paid</th><td>${esc(mmk(r.amount))}</td></tr></table>`;
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  a.download = `${r.invoice}.html`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function RecordsPage() {
  const { payments } = useAdminData();

  const approved = useMemo(
    () => payments.filter((p) => p.status === "approved").sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()),
    [payments],
  );
  const revenue = approved.reduce((s, p) => s + p.amount, 0);
  const activeBusinesses = new Set(approved.map((p) => p.business)).size;

  const rows = approved.map((p) => ({
    invoice: p.ref.replace("PMT", "INV"),
    business: p.business,
    plan: p.cycleMonths > 1 ? `${p.planName}, ${p.cycleLabel.toLowerCase()}` : p.planName,
    period: `${p.dateLabel.replace(/, \d{4}$/, "")} to ${(p.periodEnd || "—").replace(/, \d{4}$/, "")}`,
    method: p.methodLabel,
    amount: p.amount,
    email: p.billingEmail,
    txid: p.txid,
    paidOn: p.dateLabel,
  }));

  const stat = (icon: string, label: string, value: string, dark = false) => (
    <div className={`o-stat${dark ? " is-dark" : ""}`}>
      <div className="o-stat__label">
        <span className="ms" aria-hidden="true">{icon}</span>
        {label}
      </div>
      <div className="o-stat__value num">{figure(value)}</div>
    </div>
  );

  return (
    <div>
      <PageHeaderActions>
        <button
          type="button"
          className="o-btn o-btn--line"
          disabled={rows.length === 0}
          onClick={() => downloadCsv("billing-records.csv", ["Invoice", "Business", "Plan", "Period", "Method", "Amount (MMK)"], rows.map((r) => [r.invoice, r.business, r.plan, r.period, r.method, String(r.amount)]))}
        >
          <span className="ms" aria-hidden="true">download</span>
          Export CSV
        </button>
      </PageHeaderActions>

      <div className="o-kpis">
        {stat("payments", "Total collected", mmk(revenue), true)}
        {stat("receipt_long", "Total invoices", approved.length.toLocaleString())}
        {stat("workspace_premium", "Active subscriptions", activeBusinesses.toLocaleString())}
      </div>

      <section className="o-card">
        {rows.length === 0 ? (
          <div className="o-empty">
            <span className="ms" aria-hidden="true">receipt_long</span>
            <strong>No billing records yet</strong>
            <p>Approved payments show up here.</p>
          </div>
        ) : (
          <div className="o-table" style={{ ["--cols" as string]: "0.9fr 1.5fr 1.3fr 1.4fr 1.1fr 1fr 44px" }}>
            <div className="o-table__head">
              <span>Invoice</span>
              <span>Business</span>
              <span>Plan</span>
              <span>Period</span>
              <span>Method</span>
              <span>Amount</span>
              <span />
            </div>
            {rows.map((r) => (
              <div key={r.invoice} className="o-table__row" style={{ cursor: "default" }}>
                <span className="num" style={{ fontWeight: 800 }}>{r.invoice}</span>
                <span style={{ fontWeight: 800 }}>{r.business}</span>
                <span>{r.plan}</span>
                <span style={{ color: "var(--text2)" }}>{r.period}</span>
                <span>{r.method}</span>
                <span className="num" style={{ fontWeight: 800 }}>{mmk(r.amount)}</span>
                <button type="button" className="o-icon-btn" aria-label={`Download ${r.invoice}`} onClick={() => downloadInvoice(r)}>
                  <span className="ms" aria-hidden="true">download</span>
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
